/**
 * Books Service
 * =============
 * All Firestore read/write operations for book listings.
 * Components use this service — never Firestore directly.
 *
 * Pattern: every function returns ApiResult<T> so errors are typed and handled.
 *
 * Security: Firestore Rules enforce ownership server-side.
 * Client-side checks here are for UX only — not security.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  GeoPoint,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import DOMPurify from "dompurify";
import { db } from "@/lib/firebase";
import type {
  Book,
  BookSummary,
  BookFilters,
  BookCategory,
  ApiResult,
  MediaItem,
} from "@/types";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const BOOKS_COLLECTION = "books";
const PAGE_SIZE        = 20;

// ─── SANITIZATION ─────────────────────────────────────────────────────────────
/**
 * Strips HTML from user-provided strings before writing to Firestore.
 * Defence-in-depth: Zod already validated on submit, this is a second layer.
 */
function sanitize(val: string): string {
  return DOMPurify.sanitize(val, { ALLOWED_TAGS: [] }).trim();
}

// ─── CONVERTERS ───────────────────────────────────────────────────────────────
/**
 * Converts a Firestore document snapshot to a typed Book object.
 * Handles Firestore Timestamp → Date conversion.
 */
function docToBook(
  snap: DocumentSnapshot | QueryDocumentSnapshot
): Book | null {
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    ...d,
    id:         snap.id,
    createdAt:  d["createdAt"]?.toDate?.() ?? new Date(),
    updatedAt:  d["updatedAt"]?.toDate?.() ?? new Date(),
    // GeoPoint is kept as-is; components use distanceMiles (computed)
    location:   {
      lat:  d["location"]?.latitude  ?? 0,
      lng:  d["location"]?.longitude ?? 0,
      area: d["locationArea"]        ?? "",
    },
    media:      d["media"] ?? [],
    coverUrl:   d["media"]?.[0]?.url ?? null,
  } as Book;
}

function bookToSummary(book: Book): BookSummary {
  return {
    id:               book.id,
    title:            book.title,
    author:           book.author,
    category:         book.category,
    condition:        book.condition,
    coverUrl:         book.coverUrl,
    rating:           book.rating,
    reviewCount:      book.reviewCount,
    distanceMiles:    book.distanceMiles ?? 0,
    deliveryType:     book.deliveryType,
    maxDurationWeeks: book.maxDurationWeeks,
    isAvailable:      book.isAvailable,
    isNew:            book.isNew,
    ownerId:          book.ownerId,
    ownerInitials:    book.ownerInitials,
  };
}

// ─── CREATE BOOK ─────────────────────────────────────────────────────────────
export interface CreateBookPayload {
  id?:              string;
  title:            string;
  author:           string;
  description:      string;
  category:         BookCategory;
  condition:        Book["condition"];
  tags:             string[];
  media:            MediaItem[];
  deliveryType:     Book["deliveryType"];
  maxDurationWeeks: number;
  allowExtension:   boolean;
  reminderMethods:  Book["reminderMethods"];
  ownerId:          string;
  ownerName:        string;
  ownerInitials:    string;
  locationArea:     string;
  lat:              number;
  lng:              number;
}

export async function createBook(
  payload: CreateBookPayload
): Promise<ApiResult<string>> {
  try {
    const docRef = payload.id
      ? doc(db, BOOKS_COLLECTION, payload.id)
      : doc(collection(db, BOOKS_COLLECTION));
    const data = {
      title:            sanitize(payload.title),
      author:           sanitize(payload.author),
      description:      sanitize(payload.description),
      category:         payload.category,
      condition:        payload.condition,
      tags:             payload.tags.map(sanitize),
      media:            payload.media,
      coverUrl:         payload.media[0]?.url ?? null,
      deliveryType:     payload.deliveryType,
      maxDurationWeeks: payload.maxDurationWeeks,
      allowExtension:   payload.allowExtension,
      reminderMethods:  payload.reminderMethods,
      ownerId:          payload.ownerId,
      ownerName:        sanitize(payload.ownerName),
      ownerInitials:    payload.ownerInitials,
      locationArea:     sanitize(payload.locationArea),
      location:         new GeoPoint(payload.lat, payload.lng),
      rating:           0,
      reviewCount:      0,
      isAvailable:      true,
      isNew:            true,
      createdAt:        serverTimestamp(),
      updatedAt:        serverTimestamp(),
    };
    await setDoc(docRef, data);
    return { ok: true, data: docRef.id };
  } catch (err) {
    console.error("[createBook]", err);
    return { ok: false, code: "create-failed", message: "Failed to list book. Please try again." };
  }
}

// ─── FETCH SINGLE BOOK ───────────────────────────────────────────────────────
export async function fetchBook(bookId: string): Promise<ApiResult<Book>> {
  try {
    const snap = await getDoc(doc(db, BOOKS_COLLECTION, bookId));
    const book = docToBook(snap);
    if (!book) return { ok: false, code: "not-found", message: "Book not found." };
    return { ok: true, data: book };
  } catch (err) {
    console.error("[fetchBook]", err);
    return { ok: false, code: "fetch-failed", message: "Failed to load book." };
  }
}

// ─── FETCH BOOKS LIST ─────────────────────────────────────────────────────────
export interface FetchBooksOptions {
  filters:   BookFilters;
  pageSize?: number;
  cursor?:   QueryDocumentSnapshot | null;
}

export interface BooksPage {
  books:      BookSummary[];
  nextCursor: QueryDocumentSnapshot | null;
  hasMore:    boolean;
}

export async function fetchBooks(
  options: FetchBooksOptions
): Promise<ApiResult<BooksPage>> {
  try {
    const { filters, pageSize = PAGE_SIZE, cursor } = options;
    const booksRef = collection(db, BOOKS_COLLECTION);

    // Build query constraints
    const constraints: any[] = [
      where("isAvailable", "==", true),
    ];

    if (filters.category !== "all") {
      constraints.push(where("category", "==", filters.category));
    }

    if (filters.deliveryType !== "all") {
      constraints.push(where("deliveryType", "in",
        filters.deliveryType === "both" ? ["meetup", "deliver", "both"] : [filters.deliveryType, "both"]
      ));
    }

    // Sort
    const sortField =
      filters.sortBy === "rating"  ? "rating"    :
      filters.sortBy === "newest"  ? "createdAt" :
      filters.sortBy === "title"   ? "title"     :
      "createdAt"; // distance sort is done client-side after fetching

    constraints.push(
      orderBy(sortField, filters.sortBy === "title" ? "asc" : "desc"),
      limit(pageSize + 1) // fetch one extra to detect if there are more
    );

    if (cursor) constraints.push(startAfter(cursor));

    const q         = query(booksRef, ...constraints);
    const snapshot  = await getDocs(q);
    const docs      = snapshot.docs;
    const hasMore   = docs.length > pageSize;
    const pageDocs  = hasMore ? docs.slice(0, pageSize) : docs;

    let books = pageDocs
      .map((d) => docToBook(d))
      .filter((b): b is Book => b !== null)
      .map(bookToSummary);

    // Client-side search filter (Firestore doesn't support full-text search)
    if (filters.searchQuery.trim()) {
      const q = filters.searchQuery.toLowerCase();
      books = books.filter(
        (b) =>
          b.title.toLowerCase().includes(q) ||
          b.author.toLowerCase().includes(q) ||
          b.category.toLowerCase().includes(q)
      );
    }

    return {
      ok:   true,
      data: {
        books,
        nextCursor: hasMore ? pageDocs[pageDocs.length - 1]! : null,
        hasMore,
      },
    };
  } catch (err) {
    console.error("[fetchBooks]", err);
    return { ok: false, code: "fetch-failed", message: "Failed to load books." };
  }
}

// ─── FETCH USER'S BOOKS ──────────────────────────────────────────────────────
export async function fetchMyBooks(
  userId: string
): Promise<ApiResult<BookSummary[]>> {
  try {
    const q        = query(
      collection(db, BOOKS_COLLECTION),
      where("ownerId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    const books    = snapshot.docs
      .map((d) => docToBook(d))
      .filter((b): b is Book => b !== null)
      .map(bookToSummary);
    return { ok: true, data: books };
  } catch (err) {
    console.error("[fetchMyBooks]", err);
    return { ok: false, code: "fetch-failed", message: "Failed to load your books." };
  }
}

// ─── UPDATE BOOK ─────────────────────────────────────────────────────────────
export async function updateBook(
  bookId: string,
  updates: Partial<Pick<Book, "title" | "author" | "description" | "isAvailable" | "deliveryType">>
): Promise<ApiResult<void>> {
  try {
    const sanitised: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (updates.title)       sanitised["title"]       = sanitize(updates.title);
    if (updates.author)      sanitised["author"]      = sanitize(updates.author);
    if (updates.description) sanitised["description"] = sanitize(updates.description);
    if (updates.isAvailable !== undefined) sanitised["isAvailable"] = updates.isAvailable;
    if (updates.deliveryType)              sanitised["deliveryType"] = updates.deliveryType;

    await updateDoc(doc(db, BOOKS_COLLECTION, bookId), sanitised);
    return { ok: true, data: undefined };
  } catch (err) {
    console.error("[updateBook]", err);
    return { ok: false, code: "update-failed", message: "Failed to update book." };
  }
}

// ─── DELETE BOOK ─────────────────────────────────────────────────────────────
// Soft delete: set isAvailable=false (preferred)
// Hard delete: removes the document (media cleanup via Cloud Function)
export async function softDeleteBook(bookId: string): Promise<ApiResult<void>> {
  return updateBook(bookId, { isAvailable: false });
}

export async function hardDeleteBook(bookId: string): Promise<ApiResult<void>> {
  try {
    await deleteDoc(doc(db, BOOKS_COLLECTION, bookId));
    // Cloud Function triggers on delete to clean up media files
    return { ok: true, data: undefined };
  } catch (err) {
    console.error("[hardDeleteBook]", err);
    return { ok: false, code: "delete-failed", message: "Failed to delete book." };
  }
}
